/**
 * Real-Time Unit Economics Widget — compact dashboard surface.
 *
 * Three things every other FinOps tool fails to surface at-a-glance:
 *   1. Live trace volume + cost (last 24h)
 *   2. Top cost-per-request features (which features cost the most per call)
 *   3. Top customers by gross-margin contribution (who's paying for the infra)
 *
 * Auto-polls every 15s for the "real-time" feel — pulse indicator confirms
 * the freshness so the user trusts what they see. Drills into /unit-econ
 * for the full surface.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Activity, Zap, DollarSign, AlertOctagon, ArrowUpRight, Users, Layers } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 15_000;

const fmtUsd = (n) => `$${(n || 0).toFixed(Math.abs(n) < 0.1 ? 4 : 2)}`;
const fmtMs = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${n}ms`);

export default function RealTimeUnitEconWidget() {
  const [stats, setStats] = useState(null);
  const [features, setFeatures] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [statsRes, featRes, custRes] = await Promise.all([
        fetch(`${API}/traces/stats/summary?window_hours=24`, { headers }),
        fetch(`${API}/unit-econ/feature-rollup`, { headers }),
        fetch(`${API}/unit-econ/customer-rollup`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (featRes.ok) {
        const d = await featRes.json();
        const sorted = (d.features || []).slice().sort((a, b) => (b.cost_per_request_usd || 0) - (a.cost_per_request_usd || 0));
        setFeatures(sorted.slice(0, 3));
      }
      if (custRes.ok) {
        const d = await custRes.json();
        const sorted = (d.customers || []).slice().sort((a, b) => (b.mrr_usd || 0) - (a.mrr_usd || 0));
        setCustomers(sorted.slice(0, 3));
      }
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError(String(e.message || e));
    }
  }, []);

  useEffect(() => {
    fetchAll();
    intervalRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [fetchAll]);

  if (!stats && !error) {
    return (
      <Card className="bg-slate-900 border-slate-800/60 animate-pulse" data-testid="rt-unit-econ-loading">
        <CardContent className="p-6 h-40" />
      </Card>
    );
  }

  return (
    <Card className="bg-slate-950 border-emerald-500/30 overflow-hidden" data-testid="rt-unit-econ-widget">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-300" />
              Real-Time Unit Economics
              <Badge className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 text-[9px] font-mono tracking-widest ml-1">LIVE</Badge>
            </CardTitle>
            <p className="text-xs text-slate-200 mt-1">
              Trace-stream rollup · last 24h · refreshes every 15s
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5 text-cyan-300" data-testid="rt-fresh-indicator">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
              </span>
              {lastUpdated ? lastUpdated.toLocaleTimeString('en-US', { hour12: false }) : '—'}
            </div>
            <Link to="/unit-econ" className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1 group font-semibold" data-testid="rt-view-full-link">
              View full <ArrowUpRight className="w-3 h-3 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && <div className="text-rose-300 text-sm mb-3" data-testid="rt-error">{error}</div>}

        {/* 4 KPI tiles */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5" data-testid="rt-kpi-row">
            <div className="rounded-lg bg-slate-900 border border-cyan-500/30 px-3 py-2.5">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-0.5 flex items-center gap-1"><Activity className="w-2.5 h-2.5" /> Traces</div>
              <div className="text-xl font-bold text-white tabular-nums leading-tight">{stats.trace_count?.toLocaleString() || 0}</div>
            </div>
            <div className="rounded-lg bg-slate-900 border border-emerald-500/30 px-3 py-2.5">
              <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-0.5 flex items-center gap-1"><DollarSign className="w-2.5 h-2.5" /> LLM Cost</div>
              <div className="text-xl font-bold text-emerald-300 tabular-nums leading-tight">{fmtUsd(stats.total_cost_usd)}</div>
            </div>
            <div className="rounded-lg bg-slate-900 border border-cyan-500/30 px-3 py-2.5">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-0.5 flex items-center gap-1"><Zap className="w-2.5 h-2.5" /> p95 latency</div>
              <div className="text-xl font-bold text-white tabular-nums leading-tight">{fmtMs(stats.p95_latency_ms || 0)}</div>
            </div>
            <div className="rounded-lg bg-slate-900 border border-emerald-500/30 px-3 py-2.5">
              <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-0.5 flex items-center gap-1"><AlertOctagon className="w-2.5 h-2.5" /> Error rate</div>
              <div className={`text-xl font-bold tabular-nums leading-tight ${(stats.error_rate || 0) > 0.05 ? 'text-rose-300' : 'text-emerald-300'}`}>
                {((stats.error_rate || 0) * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        )}

        {/* 2-column: top features × top customers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top 3 features by $/request */}
          <div className="rounded-lg bg-slate-900 border border-emerald-500/20 p-3" data-testid="rt-top-features">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest flex items-center gap-1"><Layers className="w-2.5 h-2.5" /> Top features · cost / request</div>
              <span className="text-[9px] text-cyan-300 font-mono">live</span>
            </div>
            {features.length === 0 ? (
              <div className="text-xs text-slate-300 py-2">No traces yet — ingest spans to populate.</div>
            ) : (
              <div className="space-y-1.5">
                {features.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-xs gap-2">
                    <span className="text-white truncate font-medium">{f.feature}</span>
                    <span className="text-slate-300 shrink-0 font-mono">
                      {f.requests_24h?.toLocaleString()} req · <span className="text-cyan-300 font-bold">${(f.cost_per_request_usd || 0).toFixed(6)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top 3 customers by MRR */}
          <div className="rounded-lg bg-slate-900 border border-cyan-500/20 p-3" data-testid="rt-top-customers">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest flex items-center gap-1"><Users className="w-2.5 h-2.5" /> Top customers · gross margin</div>
              <span className="text-[9px] text-cyan-300 font-mono">live</span>
            </div>
            {customers.length === 0 ? (
              <div className="text-xs text-slate-300 py-2">No customer attribution yet — tag traces with customer_id.</div>
            ) : (
              <div className="space-y-1.5">
                {customers.map((c, i) => {
                  const margin = c.gross_margin_pct || 0;
                  const marginColor = margin >= 80 ? 'text-emerald-300' : margin >= 50 ? 'text-amber-300' : 'text-rose-300';
                  return (
                    <div key={i} className="flex items-center justify-between text-xs gap-2">
                      <span className="text-white truncate font-medium">{c.customer_name}</span>
                      <span className="text-slate-300 shrink-0 font-mono">
                        ${(c.mrr_usd || 0).toLocaleString()}/mo · <span className={`${marginColor} font-bold`}>{margin.toFixed(1)}%</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
